import { LoadingService } from '@accesso/adk-portal-shared';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { ProductService } from '../../../core/services/product.service';
import { NavigationProduct, ProductStatus, Tag } from '../../../shared/models/product.model';
import { BreadcrumbsComponent } from '../../breadcrumbs/breadcrumbs.component';
import { ChatboxComponent } from '../../chatbox/chatbox.component';
import { ProductCardsComponent } from '../../product-cards/product-cards.component';
import { SidebarComponent } from '../sidebar/sidebar.component';

@Component({
  selector: 'app-portal',
  templateUrl: './portal.component.html',
  styleUrls: ['./portal.component.scss'],
  standalone: true,
  imports: [ProductCardsComponent, BreadcrumbsComponent, RouterOutlet, SidebarComponent, ChatboxComponent],
})
export class PortalComponent implements OnInit, OnDestroy {
  navigationProducts: NavigationProduct[] = [];
  selectedProductTree: NavigationProduct[] = [];
  isAdvancedMenuEnabled = false;
  loading = false;

  @ViewChild('contentWrapper') contentWrapper: ElementRef;

  private destroy$ = new Subject<void>();

  constructor(
    private productService: ProductService,
    private featureFlagService: FeatureFlagService,
    private loadingService: LoadingService,
    public router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe(({ navigationItems }) => {
      this.loadingService.loadingOn();

      // Get the advanced menu param from URL if present
      const advancedMenuParam = this.route.snapshot.queryParamMap.get('isAdvancedMenuEnabled');
      const isAdvancedMenuParamEnabled = advancedMenuParam === 'true';

      this.featureFlagService
        .getAllowedFeatureFlagByTag('MENU_REFACTOR')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (allowed) => {
            if (allowed && isAdvancedMenuParamEnabled) {
              this.handleAdvancedMenu(navigationItems);
            } else {
              this.handleBasicMenu(navigationItems);
            }
            this.loading = false;
            this.loadingService.loadingOff();
          },
          error: () => {
            this.loading = false;
            this.loadingService.loadingOff();
            this.handleBasicMenu(navigationItems);
            console.error('Error fetching feature flag for MENU_REFACTOR');
          },
        });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleAdvancedMenu(navigationItems: NavigationProduct[]): void {
    this.isAdvancedMenuEnabled = true;
    this.navigationProducts = this.productService.flattenNavigationProducts(navigationItems).map((product) => ({
      ...product,
      tags: product.tags?.filter((tag) => tag.editStatus !== ProductStatus.INACTIVE) || [],
    }));
    this.updateSelectedProductFromUrl();
  }

  private handleBasicMenu(navigationItems: NavigationProduct[]): void {
    this.navigationProducts = navigationItems.map((product) => ({
      ...product,
      tags: product.tags?.filter((tag) => tag.editStatus !== ProductStatus.INACTIVE) || [],
    }));
    this.selectedProductTree = this.navigationProducts;
    if (this.router.url === '/portal') {
      this.navigateToFirstPageInProducts(this.navigationProducts);
    }
  }

  navigateToFirstPageInProducts(products: NavigationProduct[]): void {
    for (const product of products) {
      if (product.pages && product.pages.length > 0) {
        const firstPage = product.pages[0];
        this.router.navigate(['portal/' + firstPage.fullRoute.slice(1)]);
        return;
      }
      if (product.childProducts && product.childProducts.length > 0) {
        return this.navigateToFirstPageInProducts(product.childProducts);
      }
    }
  }

  navigateToProduct(product: NavigationProduct): void {
    this.selectedProductTree = [product];
    if (product.pages && product.pages.length > 0) {
      if (product.pages[0].fullRoute) {
        // defaulted to first page's route
        const routePath = product.pages[0].fullRoute.startsWith('/') ? product.pages[0].fullRoute.slice(1) : product.pages[0].fullRoute;

        this.router.navigate([routePath], {
          relativeTo: this.route,
          queryParams: this.isAdvancedMenuEnabled ? { isAdvancedMenuEnabled: true } : undefined,
          queryParamsHandling: 'merge',
        });
      }
    }
  }

  updateSelectedProductFromUrl(): void {
    // Extract product Route from URL
    const urlSegments = this.router.url.split('/');

    urlSegments.forEach((segment) => {
      if (segment !== '') {
        const productRoute = '/' + segment;
        const foundProduct = this.findProductByRoute(this.navigationProducts, productRoute);
        if (foundProduct) {
          this.selectedProductTree = [foundProduct];
        }
      }
    });
  }

  private findProductByRoute(products: NavigationProduct[], route: string): NavigationProduct | null {
    for (const product of products) {
      if (product.route === route) {
        return product;
      }
    }

    return null;
  }
}
